import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, TextField, Typography } from '@mui/material';
import InfoText from 'components/shared/info-text.component';

export type RankedCategory = { category: string; name?: string | null };

type PriceRequestCategoryRanksProps = {
    categories: RankedCategory[];
    ranks: Record<string, string>;
    onChange: (category: string, rank: string) => void;
};

/**
 * DD-3822: the lab ranking for each category of the draft the lab has no price list for yet.
 * Approving a price in such a category adds it to the lab's price list with this rank.
 */
const PriceRequestCategoryRanks: FC<PriceRequestCategoryRanksProps> = ({ categories, ranks, onChange }) => {
    const { t: translate } = useTranslation('price-requests');
    if (!categories.length) return null;
    return (
        <Box mt={2} data-test="price-request.category-ranks">
            <Typography variant="body2" fontWeight={600} mb={1}>
                {translate('CATEGORY_RANKS')}
            </Typography>
            <InfoText text={translate('CATEGORY_RANKS_INFO')} />
            <Box display="flex" flexWrap="wrap" gap={2} mt={1.5}>
                {categories.map(({ category, name }) => (
                    <TextField
                        key={category}
                        required
                        size="small"
                        type="number"
                        label={translate('LAB_RANKING_FOR', { category: name || category })}
                        value={ranks[category] ?? ''}
                        onChange={(event) => onChange(category, event.target.value)}
                        inputProps={{ min: 1, step: 1, 'data-test': `price-request.category-rank-${category}` }}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default PriceRequestCategoryRanks;
